import { memo } from 'react'
import { ButtonBase, SxProps } from '@mui/material'
import { PlusIcon } from '@/assets'
import { useTranslations } from '@/core/translation'
import { PASSENGER_AGE } from '@/utils'
import { Box } from '@/components/Layout'
import { Counter } from '@/components/Inputs'
import Text from '../Text'
import Divider from '../Divider'
import Dropdown, { DropdownList } from '../Dropdown'
import { personSelectorStyle as styles } from './PersonSelector.style'
import { PersonSelectorProps } from './PersonSelector.types'
import { ageOptions, MAX_PERSONS_PER_ROOM } from './PersonSelector.data'
import { usePersonSelectorController } from './PersonSelector.controller'

const PersonSelector = ({
  label,
  rooms,
  onChange,
  roomSelector = true,
  dropdownProps = {},
  childAgeDropdownProps = {},
  maxPersonLimit = MAX_PERSONS_PER_ROOM,
}: PersonSelectorProps) => {
  const t = useTranslations('personSelector')
  const {
    summaryLabel,
    onAdultsChange,
    onChildrenChange,
    toggleChildAgeDropdown,
    onChildrenAgeChange,
    addRoom,
    removeRoom,
  } = usePersonSelectorController({ rooms, onChange, roomSelector })

  const { popperProps: dropdownPopperProps = {}, ...otherDropdownProps } = dropdownProps
  const { wrapperSx: dropdownPopperWrapperSxProps = {}, ...otherDropdownPopperProps } =
    dropdownPopperProps

  const { popperProps: childAgeDropdownPopperProps = {}, ...otherChildAgeDropdownProps } =
    childAgeDropdownProps
  const {
    wrapperSx: childAgeDropdownPopperWrapperSxProps = {},
    ...otherChildAgeDropdownPopperProps
  } = childAgeDropdownPopperProps

  return (
    <Dropdown
      label={summaryLabel}
      labelHeading={label}
      fullWidth
      popperProps={{
        wrapperSx: { ...styles.dropdownWrapperSx, ...dropdownPopperWrapperSxProps } as SxProps,
        ...otherDropdownPopperProps,
      }}
      {...otherDropdownProps}>
      <Box sx={styles.mainWrapper}>
        {rooms.map((room, roomIndex) => (
          <Box key={room.id} sx={styles.roomWrapper}>
            {roomSelector && (
              <Text sx={styles.room}>{t('room', { roomNumber: roomIndex + 1 })}</Text>
            )}

            <Box sx={styles.adultsWrapper}>
              <Text sx={styles.adultsText}>{t('adults')}</Text>
              <Counter
                value={room.adults}
                onChange={(value) => onAdultsChange(roomIndex, value)}
                min={1}
                max={maxPersonLimit - room.children}
              />
            </Box>

            <Box sx={styles.childrenWrapper}>
              <Box sx={styles.childSelectorWrapper}>
                <Box>
                  <Text sx={styles.adultsText}>{t('children')}</Text>
                  <Text sx={styles.childrenAgeText}>
                    {`${t('age')} ${PASSENGER_AGE.START}-${PASSENGER_AGE.MINOR_CUTOFF}`}
                  </Text>
                </Box>
                <Counter
                  value={room.children}
                  onChange={(value) => onChildrenChange(roomIndex, value)}
                  min={0}
                  max={maxPersonLimit - room.adults}
                />
              </Box>

              {room.childrenAges.map((selectedOption, childIndex) => (
                <Dropdown
                  popperProps={{
                    wrapperSx: {
                      ...styles.dropdownWrapperSx,
                      ...childAgeDropdownPopperWrapperSxProps,
                    } as SxProps,
                    ...otherChildAgeDropdownPopperProps,
                  }}
                  key={childIndex}
                  label={selectedOption.label}
                  labelHeading={t('childAgeLabel', { index: childIndex + 1 })}
                  fullWidthWithSpace
                  open={room.dropdownOpen[childIndex]}
                  onClick={() => toggleChildAgeDropdown(roomIndex, childIndex, true)}
                  onClose={() => toggleChildAgeDropdown(roomIndex, childIndex, false)}
                  {...otherChildAgeDropdownProps}>
                  <DropdownList
                    options={ageOptions}
                    idKey="value"
                    labelKey="label"
                    valueKey="value"
                    value={selectedOption.value}
                    onChange={(option) => {
                      onChildrenAgeChange(roomIndex, childIndex, option)
                      toggleChildAgeDropdown(roomIndex, childIndex, false)
                    }}
                  />
                </Dropdown>
              ))}
            </Box>

            {roomSelector && rooms.length > 1 && (
              <ButtonBase sx={styles.addRoomButton} onClick={() => removeRoom(roomIndex)}>
                <Text sx={styles.removeRoom}>{t('removeRoom')}</Text>
              </ButtonBase>
            )}
            {rooms.length > 1 && roomIndex < rooms.length - 1 && <Divider sx={styles.divider} />}
          </Box>
        ))}

        {roomSelector && (
          <ButtonBase sx={styles.addRoomButton} onClick={addRoom}>
            <Box sx={styles.addRoomWrapper}>
              <Box sx={styles.plusIcon}>
                <PlusIcon />
              </Box>
              <Text sx={styles.removeRoom}>{t('addRoom')}</Text>
            </Box>
          </ButtonBase>
        )}
      </Box>
    </Dropdown>
  )
}

export default memo(PersonSelector)
